import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Box,
  Divider,
  Avatar,
  IconButton,
  Typography,
  CircularProgress,
  Tabs,
  Tab,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  ListItemAvatar,
  Alert,
  Snackbar,
  Tooltip,
  Chip,
} from '@mui/material';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import DeleteIcon from '@mui/icons-material/Delete';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import ShareIcon from '@mui/icons-material/Share';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { QRCodeCanvas } from 'qrcode.react';
import { usersAPI, teamsAPI, authAPI } from '../api';
import { getApiBaseUrl } from '../utils/apiConfig';
import { getAvatarInitials } from '../utils/avatar';

const API_BASE_URL = getApiBaseUrl();

function SettingsDialog({ open, onClose, currentUser, onUpdateUsername, onUsersDeleted, onTeamUpdate }) {
  const [username, setUsername] = useState(currentUser?.username || '');
  const [newTeamName, setNewTeamName] = useState('');
  const [notificationsEnabled, setNotificationsEnabled] = useState(
    localStorage.getItem('notificationsEnabled') !== 'false'
  );
  const [saving, setSaving] = useState(false);
  const [profilePicturePreview, setProfilePicturePreview] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const [removingPicture, setRemovingPicture] = useState(false);
  const fileInputRef = useRef(null);
  const [activeTab, setActiveTab] = useState(0);
  const [allUsers, setAllUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [deletingUsers, setDeletingUsers] = useState(false);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportDataText, setExportDataText] = useState('');
  const [shareLoginOpen, setShareLoginOpen] = useState(false);
  const [shareLoginUrl, setShareLoginUrl] = useState('');
  const [shareLoginExpiresInSeconds, setShareLoginExpiresInSeconds] = useState(0);
  const [generatingShareLink, setGeneratingShareLink] = useState(false);

  const isAdmin = currentUser?.username?.includes('Maik') || false;

  useEffect(() => {
    if (currentUser) {
      setUsername(currentUser.username);
      setNewTeamName('');
      // Reset preview when dialog opens/closes or user changes
      if (!open) {
        setProfilePicturePreview(null);
        setSelectedFile(null);
        setActiveTab(0);
        setSelectedUserIds([]);
        setError(null);
      } else {
        // Set current profile picture as preview if exists
        if (currentUser.profilePictureUrl) {
          const fullUrl = `${API_BASE_URL.replace('/api', '')}${currentUser.profilePictureUrl}`;
          setProfilePicturePreview(fullUrl);
        } else {
          setProfilePicturePreview(null);
        }
        // Load users if admin tab is available
        if (isAdmin && activeTab === 1) {
          loadUsers();
        }
      }
    }
  }, [currentUser, open, isAdmin, activeTab]);

  // Auto-select text in export dialog when it opens
  useEffect(() => {
    if (exportDialogOpen) {
      const timer = setTimeout(() => {
        const textArea = document.getElementById('export-data-textarea');
        if (textArea) {
          textArea.focus();
          textArea.select();
        }
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [exportDialogOpen]);

  const loadUsers = async () => {
    setLoadingUsers(true);
    setError(null);
    try {
      const [usersResponse, teamsResponse] = await Promise.all([
        usersAPI.getAll(),
        teamsAPI.getAll()
      ]);
      // Filter out current user from the list
      const otherUsers = usersResponse.data.filter(user => user.id !== currentUser?.id);
      setAllUsers(otherUsers);
      setTeams(teamsResponse.data);
    } catch (error) {
      console.error('Failed to load users:', error);
      setError('Failed to load users');
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setSelectedUserIds([]);
    setError(null);
    if (newValue === 1 && isAdmin) {
      loadUsers();
    }
  };

  const handleUserToggle = (userId) => {
    setSelectedUserIds(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAll = () => {
    if (selectedUserIds.length === allUsers.length) {
      setSelectedUserIds([]);
    } else {
      setSelectedUserIds(allUsers.map(user => user.id));
    }
  };

  const handleDeleteUsers = async () => {
    if (selectedUserIds.length === 0) return;

    const confirmMessage = `Are you sure you want to delete ${selectedUserIds.length} user(s)? This action cannot be undone.`;
    if (!window.confirm(confirmMessage)) return;

    setDeletingUsers(true);
    setError(null);
    try {
      await usersAPI.deleteUsers(selectedUserIds);
      setSelectedUserIds([]);
      // Reload users list
      await loadUsers();
      // Notify parent component to refresh users
      if (onUsersDeleted) {
        onUsersDeleted(selectedUserIds);
      }
    } catch (error) {
      console.error('Failed to delete users:', error);
      setError(error.response?.data?.error || 'Failed to delete users');
    } finally {
      setDeletingUsers(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('File size must be less than 5MB');
      return;
    }

    setSelectedFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setProfilePicturePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveProfilePicture = async () => {
    if (!currentUser) return;

    setRemovingPicture(true);
    try {
      await usersAPI.removeProfilePicture(currentUser.id);
      setProfilePicturePreview(null);
      setSelectedFile(null);
      // The socket event will update the UI
    } catch (error) {
      console.error('Failed to remove profile picture:', error);
      alert('Failed to remove profile picture. Please try again.');
    } finally {
      setRemovingPicture(false);
    }
  };

  const handleApplyProfilePicture = async () => {
    if (!currentUser || !selectedFile) return;

    setUploadingPicture(true);
    try {
      await usersAPI.uploadProfilePicture(currentUser.id, selectedFile);
      setSelectedFile(null);
      // The socket event will update the UI
    } catch (error) {
      console.error('Failed to upload profile picture:', error);
      alert('Failed to upload profile picture. Please try again.');
    } finally {
      setUploadingPicture(false);
    }
  };

  const handleNotificationsChange = async (enabled) => {
    setNotificationsEnabled(enabled);
    
    // If enabling notifications, request permission if not already granted
    if (enabled && 'Notification' in window) {
      if (Notification.permission === 'default' || Notification.permission === 'denied') {
        try {
          const permission = await Notification.requestPermission();
          if (permission !== 'granted') {
            // Permission denied, disable notifications
            setNotificationsEnabled(false);
            alert('Notification permission was denied. Please enable it in your browser settings.');
          }
        } catch (error) {
          console.error('Error requesting notification permission:', error);
          setNotificationsEnabled(false);
        }
      }
    }
  };

  const handleSave = async () => {
    if (!currentUser) return;

    setSaving(true);
    try {
      if (username.trim() !== currentUser.username) {
        await onUpdateUsername(currentUser.id, username.trim());
      }
      if (newTeamName.trim()) {
        await teamsAPI.create(newTeamName.trim());
        setNewTeamName('');
        // Refresh user data to get updated teams
        if (onTeamUpdate) {
          await onTeamUpdate();
        }
      }
      localStorage.setItem('notificationsEnabled', notificationsEnabled.toString());
      onClose();
    } catch (error) {
      console.error('Failed to save settings:', error);
    } finally {
      setSaving(false);
    }
  };


  const readFromClipboard = async () => {
    // Try modern clipboard API first
    if (navigator.clipboard && navigator.clipboard.readText) {
      try {
        return await navigator.clipboard.readText();
      } catch (error) {
        // Fall through to fallback method
      }
    }
    
    // Fallback: prompt user to paste manually
    return new Promise((resolve) => {
      const text = window.prompt('Please paste the exported data here:');
      resolve(text || '');
    });
  };

  const handleExport = () => {
    try {
      // Collect all relevant localStorage data
      const exportData = {
        token: localStorage.getItem('token'),
        notificationsEnabled: localStorage.getItem('notificationsEnabled'),
        lastReadTimestamps: localStorage.getItem('lastReadTimestamps'),
      };

      const jsonString = JSON.stringify(exportData, null, 2);
      
      // Show dialog with data for manual copying
      setExportDataText(jsonString);
      setExportDialogOpen(true);
    } catch (error) {
      console.error('Failed to export data:', error);
      // Even on error, show the data in dialog
      const exportData = {
        token: localStorage.getItem('token'),
        notificationsEnabled: localStorage.getItem('notificationsEnabled'),
        lastReadTimestamps: localStorage.getItem('lastReadTimestamps'),
      };
      setExportDataText(JSON.stringify(exportData, null, 2));
      setExportDialogOpen(true);
    }
  };

  const handleImport = async () => {
    try {
      // Read from clipboard
      const clipboardText = await readFromClipboard();
      
      if (!clipboardText || clipboardText.trim() === '') {
        throw new Error('Clipboard is empty');
      }
      
      // Parse JSON
      let importData;
      try {
        importData = JSON.parse(clipboardText);
      } catch (parseError) {
        throw new Error('Invalid JSON format');
      }

      // Validate and import data
      if (importData.token !== undefined && importData.token !== null) {
        localStorage.setItem('token', importData.token);
      }
      if (importData.notificationsEnabled !== undefined && importData.notificationsEnabled !== null) {
        localStorage.setItem('notificationsEnabled', importData.notificationsEnabled);
        setNotificationsEnabled(importData.notificationsEnabled !== 'false');
      }
      if (importData.lastReadTimestamps !== undefined && importData.lastReadTimestamps !== null) {
        localStorage.setItem('lastReadTimestamps', importData.lastReadTimestamps);
      }

      setSnackbar({
        open: true,
        message: 'Data imported successfully! Refreshing page...',
        severity: 'success',
      });

      // Refresh the page after a short delay to show the success message
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      console.error('Failed to import data:', error);
      setSnackbar({
        open: true,
        message: error.message === 'Invalid JSON format' 
          ? 'Failed to import: Invalid JSON format in clipboard'
          : error.message === 'Clipboard is empty'
          ? 'Failed to import: Clipboard is empty'
          : 'Failed to import data. Please try again.',
        severity: 'error',
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleShareLogin = async () => {
    setGeneratingShareLink(true);
    try {
      const response = await authAPI.shareLogin();
      const { shareToken, expiresInSeconds } = response.data;
      const base = `${window.location.origin}${window.location.pathname}`;
      const url = `${base}?shareLogin=${encodeURIComponent(shareToken)}`;
      setShareLoginUrl(url);
      setShareLoginExpiresInSeconds(expiresInSeconds || 0);
      setShareLoginOpen(true);
    } catch (error) {
      console.error('Failed to generate share-login link:', error);
      setSnackbar({
        open: true,
        message: 'Failed to generate share link. Please try again.',
        severity: 'error',
      });
    } finally {
      setGeneratingShareLink(false);
    }
  };

  const handleCopyShareLink = async () => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(shareLoginUrl);
        setSnackbar({ open: true, message: 'Link copied to clipboard', severity: 'success' });
      } else {
        const input = document.getElementById('share-login-url');
        if (input) {
          input.focus();
          input.select();
        }
      }
    } catch (error) {
      console.error('Failed to copy link:', error);
      setSnackbar({ open: true, message: 'Failed to copy link', severity: 'error' });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Settings</DialogTitle>
      <DialogContent>
        {isAdmin && (
          <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 2 }}>
            <Tab label="General" />
            <Tab label="Admin" />
          </Tabs>
        )}
        
        {activeTab === 0 && (
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Profile Picture Section */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <Typography variant="subtitle1">Profile Picture</Typography>
              <Box sx={{ position: 'relative' }}>
                <Avatar
                  src={profilePicturePreview || undefined}
                  sx={{
                    width: 120,
                    height: 120,
                    bgcolor: 'primary.main',
                    fontSize: '3rem',
                  }}
                >
                  {!profilePicturePreview && getAvatarInitials(currentUser?.username || '')}
                </Avatar>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  accept="image/*"
                  style={{ display: 'none' }}
                />
                <IconButton
                  color="primary"
                  aria-label="upload picture"
                  component="span"
                  onClick={() => fileInputRef.current?.click()}
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    right: 0,
                    bgcolor: 'white',
                    border: '2px solid',
                    borderColor: 'primary.main',
                    '&:hover': {
                      bgcolor: 'grey.100',
                    },
                  }}
                >
                  <PhotoCameraIcon />
                </IconButton>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {selectedFile && (
                  <Button
                    variant="contained"
                    onClick={handleApplyProfilePicture}
                    disabled={uploadingPicture}
                    size="small"
                  >
                    {uploadingPicture ? <CircularProgress size={20} /> : 'Apply'}
                  </Button>
                )}
                {(profilePicturePreview || currentUser?.profilePictureUrl) && (
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={handleRemoveProfilePicture}
                    disabled={removingPicture}
                    size="small"
                    startIcon={<DeleteIcon />}
                  >
                    {removingPicture ? <CircularProgress size={20} /> : 'Remove'}
                  </Button>
                )}
              </Box>
            </Box>

            <Divider />
            
            <TextField
              fullWidth
              label="Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              helperText="Change your display name"
            />
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="subtitle1">Teams</Typography>
              {currentUser?.teams && currentUser.teams.length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {currentUser.teams.map(team => (
                    <Box key={team.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                      <Typography variant="body2">{team.name}</Typography>
                      <Button
                        size="small"
                        color="error"
                        onClick={async () => {
                          if (window.confirm(`Are you sure you want to leave "${team.name}"?`)) {
                            try {
                              await teamsAPI.leave(team.id);
                              if (onTeamUpdate) {
                                onTeamUpdate();
                              }
                            } catch (error) {
                              console.error('Failed to leave team:', error);
                              alert('Failed to leave team. Please try again.');
                            }
                          }
                        }}
                      >
                        Leave
                      </Button>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  You are not in any teams yet.
                </Typography>
              )}
              <TextField
                fullWidth
                label="Create New Team"
                value={newTeamName}
                onChange={(e) => setNewTeamName(e.target.value)}
                helperText="Enter a name to create a new team"
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && newTeamName.trim()) {
                    e.preventDefault();
                    handleSave();
                  }
                }}
              />
            </Box>
            
            <Divider />
            
            <FormControlLabel
              control={
                <Switch
                  checked={notificationsEnabled}
                  onChange={(e) => handleNotificationsChange(e.target.checked)}
                />
              }
              label="Enable Notifications"
            />

            <Divider />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="subtitle1">Data Management</Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<FileDownloadIcon />}
                  onClick={handleExport}
                  fullWidth
                >
                  Export Data
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<FileUploadIcon />}
                  onClick={handleImport}
                  fullWidth
                >
                  Import Data
                </Button>
              </Box>
              <Typography variant="caption" color="text.secondary">
                Export/Import your login token, notification settings, and read timestamps
              </Typography>
            </Box>

            <Divider />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant="subtitle1">Share Login</Typography>
              <Button
                variant="outlined"
                startIcon={generatingShareLink ? <CircularProgress size={18} /> : <ShareIcon />}
                onClick={handleShareLogin}
                disabled={generatingShareLink}
              >
                Share Login
              </Button>
              <Typography variant="caption" color="text.secondary">
                Generate a one-time link and QR code to log in as you on another device.
                The link is valid for a short time and can only be used once.
              </Typography>
            </Box>
          </Box>
        )}

        {activeTab === 1 && isAdmin && (
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="h6">User Management</Typography>
            
            {error && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="body2">
                {selectedUserIds.length > 0 
                  ? `${selectedUserIds.length} user(s) selected`
                  : 'Select users to delete'}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  size="small"
                  onClick={handleSelectAll}
                  disabled={loadingUsers || allUsers.length === 0}
                >
                  {selectedUserIds.length === allUsers.length ? 'Deselect All' : 'Select All'}
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  size="small"
                  onClick={handleDeleteUsers}
                  disabled={selectedUserIds.length === 0 || deletingUsers || loadingUsers}
                  startIcon={deletingUsers ? <CircularProgress size={16} /> : <DeleteIcon />}
                >
                  Delete Selected
                </Button>
              </Box>
            </Box>

            {loadingUsers ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <List sx={{ maxHeight: 400, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                {allUsers.length === 0 ? (
                  <ListItem>
                    <ListItemText primary="No users found" />
                  </ListItem>
                ) : (
                  allUsers.map((user) => (
                    <ListItem
                      key={user.id}
                      disablePadding
                      secondaryAction={
                        <Checkbox
                          edge="end"
                          checked={selectedUserIds.includes(user.id)}
                          onChange={() => handleUserToggle(user.id)}
                        />
                      }
                    >
                      <ListItemButton onClick={() => handleUserToggle(user.id)}>
                        <ListItemAvatar>
                          <Avatar
                            src={user.profilePictureUrl ? `${API_BASE_URL.replace('/api', '')}${user.profilePictureUrl}` : undefined}
                            sx={{ bgcolor: 'primary.main' }}
                          >
                            {getAvatarInitials(user.username)}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body1">{user.username}</Typography>
                              {(() => {
                                const userTeamIds = user.teamIds || [];
                                const userTeams = teams.filter(team => userTeamIds.includes(team.id));
                                const teamCount = userTeams.length;
                                const teamNames = userTeams.map(team => team.name).join(', ') || 'No teams';
                                
                                return (
                                  <Tooltip title={teamNames} arrow>
                                    <Chip
                                      label={`${teamCount} team${teamCount !== 1 ? 's' : ''}`}
                                      size="small"
                                      sx={{ height: 20, fontSize: '0.7rem' }}
                                    />
                                  </Tooltip>
                                );
                              })()}
                            </Box>
                          }
                          secondary={
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                              {user.online && (
                                <Typography variant="caption" color="success.main">
                                  Online
                                </Typography>
                              )}
                              {user.away && (
                                <Typography variant="caption" color="warning.main">
                                  Away
                                </Typography>
                              )}
                              {!user.online && !user.away && (
                                <Typography variant="caption" color="text.secondary">
                                  Offline
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                  ))
                )}
              </List>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {activeTab === 0 && (
          <Button onClick={handleSave} variant="contained" disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        )}
      </DialogActions>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Export Data Dialog */}
      <Dialog open={exportDialogOpen} onClose={() => setExportDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Export Data</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Press <strong>Ctrl+A</strong> to select all, then <strong>Ctrl+C</strong> to copy.
          </Typography>
          <TextField
            id="export-data-textarea"
            multiline
            fullWidth
            rows={15}
            value={exportDataText}
            onFocus={(e) => {
              // Select all text when focused
              e.target.select();
            }}
            sx={{
              '& .MuiInputBase-input': {
                fontFamily: 'monospace',
                fontSize: '0.875rem',
              },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Share Login Dialog */}
      <Dialog
        open={shareLoginOpen}
        onClose={() => setShareLoginOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Share Login</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, pt: 1 }}>
            <Alert severity="warning" sx={{ width: '100%' }}>
              Anyone who uses this link or scans this QR code will be logged in as
              <strong> {currentUser?.username}</strong>. The link can only be used once
              and expires in about {Math.round((shareLoginExpiresInSeconds || 0) / 60)} minutes.
            </Alert>
            {shareLoginUrl && (
              <Box
                sx={{
                  p: 2,
                  bgcolor: 'white',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <QRCodeCanvas value={shareLoginUrl} size={220} includeMargin={false} />
              </Box>
            )}
            <TextField
              id="share-login-url"
              fullWidth
              value={shareLoginUrl}
              InputProps={{ readOnly: true }}
              onFocus={(e) => e.target.select()}
              size="small"
            />
            <Button
              variant="outlined"
              startIcon={<ContentCopyIcon />}
              onClick={handleCopyShareLink}
              fullWidth
            >
              Copy Link
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShareLoginOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}

export default SettingsDialog;

